import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar
} from '@mui/material';
import {
    CheckCircle,
    Star,
    Warning
} from '@mui/icons-material';
import { FddApi } from '../../ui/services/server-fdd';

export const PlansPage = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [changingPlan, setChangingPlan] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [validationErrors, setValidationErrors] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const result = await FddApi.getAvailablePlans();
            if (!result.hasError) {
                setPlans(result.plans);
            } else {
                setError('Error al cargar planes');
            }
        } catch (err) {
            console.error('[Plans] Error fetching plans:', err);
            setError('No se pudieron cargar los planes. Por favor intenta más tarde.');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price) => {
        if (price === 0 || price === '0.00') return 'Gratis';
        return `$${parseFloat(price).toFixed(2)} USD`;
    };

    const handleSelectPlan = (plan) => {
        // Verificar si el usuario está autenticado
        const token = localStorage.getItem('authToken');
        if (!token) {
            setSnackbar({
                open: true,
                message: 'Debes iniciar sesión para cambiar de plan',
                severity: 'warning'
            });
            setTimeout(() => navigate('/login'), 2000);
            return;
        }

        setSelectedPlan(plan);
        setValidationErrors(null);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedPlan(null);
        setValidationErrors(null);
    };

    const handleConfirmPlanChange = async () => {
        if (!selectedPlan) return;

        setChangingPlan(true);

        try {
            // Plan de pago → redirigir a PayPal
            if (selectedPlan.monthlyPrice > 0) {
                const result = await FddApi.createSubscription(selectedPlan.name);

                if (result.hasError) {
                    setSnackbar({
                        open: true,
                        message: result.error || 'Error al iniciar el pago',
                        severity: 'error'
                    });
                    setModalOpen(false);
                } else {
                    // Redirigir al usuario a PayPal para aprobar el pago
                    window.location.href = result.approvalUrl;
                }
                return;
            }

            // Plan gratuito → cambio directo sin pago
            const result = await FddApi.changePlan(selectedPlan.id);

            if (result.hasError) {
                if (result.statusCode === 409 && result.validationErrors) {
                    setValidationErrors(result.validationErrors);
                    setChangingPlan(false);
                    return;
                }

                setSnackbar({
                    open: true,
                    message: result.error || 'Error al cambiar el plan',
                    severity: 'error'
                });
                setModalOpen(false);
            } else {
                setSnackbar({
                    open: true,
                    message: result.message || 'Plan cambiado exitosamente',
                    severity: 'success'
                });
                setModalOpen(false);
                setTimeout(() => navigate('/account'), 2000);
            }
        } catch (error) {
            console.error('[CHANGE PLAN] Error:', error);
            setSnackbar({
                open: true,
                message: 'Error al procesar la solicitud',
                severity: 'error'
            });
            setModalOpen(false);
        } finally {
            setChangingPlan(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 2, px: { xs: 2, sm: 3, md: 4 }, mt: 2 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                    Elige el plan perfecto para ti
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    Firma digital segura y profesional desde cualquier lugar
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Precios en dólares estadounidenses (USD) • Facturación mensual
                </Typography>
            </Box>

            {/* Tarjetas de Planes */}
            <Grid container spacing={4} alignItems="stretch" justifyContent="center">
                {plans.map((plan) => {
                    return (
                        <Grid item xs={12} sm={6} md={4} key={plan.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Card
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    maxWidth: 380,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    border: plan.isPopular ? 3 : 1,
                                    borderColor: plan.isPopular ? 'primary.main' : 'divider',
                                    transform: plan.isPopular ? 'scale(1.05)' : 'none',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: plan.isPopular ? 'scale(1.08)' : 'scale(1.03)',
                                        boxShadow: 6
                                    }
                                }}
                            >
                                {plan.isPopular && (
                                    <Chip
                                        icon={<Star />}
                                        label="Más Popular"
                                        color="primary"
                                        sx={{
                                            position: 'absolute',
                                            top: -12,
                                            right: 16,
                                            fontWeight: 'bold'
                                        }}
                                    />
                                )}

                                <CardContent sx={{ flexGrow: 1, pt: plan.isPopular ? 3 : 2, pb: 1 }}>
                                    {/* Nombre del Plan */}
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        {plan.name}
                                    </Typography>

                                    {/* Descripción */}
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                                        {plan.description || `Plan ${plan.name} para firma digital`}
                                    </Typography>

                                    {/* Precio */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="h3" fontWeight="bold" component="span">
                                            {formatPrice(plan.monthlyPrice)}
                                        </Typography>
                                        {plan.monthlyPrice > 0 && (
                                            <Typography variant="body1" color="text.secondary" component="span">
                                                /mes
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Características principales resumen */}
                                    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                        <Typography variant="body2" fontWeight="600" gutterBottom>
                                            {plan.documentsLimit ? `${plan.documentsLimit} documentos/mes` : '📄 Documentos ilimitados'}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="600" gutterBottom>
                                            {plan.signersLimit ? `${plan.signersLimit} firmantes/doc` : '✍️ Firmantes ilimitados'}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="600">
                                            💾 {Math.round(plan.storageLimitMB / 1024)}GB almacenamiento
                                        </Typography>
                                    </Box>

                                    {/* Features detalladas */}
                                    <List dense sx={{ mt: 2 }}>
                                        {plan.features && plan.features.length > 0 && (
                                            <>
                                                {plan.features
                                                    .filter(feature => 
                                                        !feature.includes('Unlimited Documents') && 
                                                        !feature.includes('Unlimited co-signers') &&
                                                        !feature.includes('Secure Cloud')
                                                    )
                                                    .map((feature, idx) => (
                                                        <ListItem key={idx} disablePadding sx={{ mb: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckCircle color="success" fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={feature}
                                                                primaryTypographyProps={{ fontSize: '0.8rem' }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                            </>
                                        )}
                                    </List>
                                </CardContent>

                                <CardActions sx={{ p: 2, pt: 0 }}>
                                    <Button
                                        fullWidth
                                        variant={plan.isPopular ? 'contained' : 'outlined'}
                                        size="large"
                                        sx={{ py: 1.5, fontWeight: 'bold' }}
                                        onClick={() => handleSelectPlan(plan)}
                                    >
                                        {plan.monthlyPrice === 0 ? 'Comenzar Gratis' : 'Elegir Plan'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* FAQs o información adicional */}
            <Box sx={{ mt: 8, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    ¿Necesitas más información? <Button variant="text">Contáctanos</Button>
                </Typography>
            </Box>

            {/* Modal de Confirmación de Cambio de Plan */}
            <Dialog
                open={modalOpen}
                onClose={!changingPlan ? handleCloseModal : undefined}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {validationErrors
                        ? 'No puedes cambiar a este plan'
                        : selectedPlan?.monthlyPrice > 0
                            ? 'Confirmar Suscripción'
                            : 'Confirmar Cambio de Plan'
                    }
                </DialogTitle>
                <DialogContent>
                    {validationErrors ? (
                        // Mostrar errores de validación
                        <Box>
                            <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
                                {validationErrors.length > 0 && validationErrors[0].type === 'documents' ? (
                                    <Typography variant="body2">
                                        <strong>Límite de documentos excedido:</strong><br />
                                        Has usado <strong>{validationErrors.find(e => e.type === 'documents')?.current}</strong> documentos, 
                                        pero el plan {selectedPlan?.name} solo permite <strong>{validationErrors.find(e => e.type === 'documents')?.limit}</strong>.
                                    </Typography>
                                ) : null}
                                
                                {validationErrors.find(e => e.type === 'storage') && (
                                    <Typography variant="body2" sx={{ mt: validationErrors.find(e => e.type === 'documents') ? 2 : 0 }}>
                                        <strong>Límite de almacenamiento excedido:</strong><br />
                                        Has usado <strong>{(validationErrors.find(e => e.type === 'storage')?.current / 1024).toFixed(2)} GB</strong>, 
                                        pero el plan {selectedPlan?.name} solo permite <strong>{(validationErrors.find(e => e.type === 'storage')?.limit / 1024).toFixed(2)} GB</strong>.
                                    </Typography>
                                )}
                            </Alert>

                            <DialogContentText>
                                <strong>Sugerencia:</strong> Espera al inicio del próximo período de facturación (cuando se reinicie tu uso mensual) 
                                o elige un plan con límites mayores que se ajusten a tu uso actual.
                            </DialogContentText>
                        </Box>
                    ) : (
                        // Confirmación normal
                        <DialogContentText component="div">
                            ¿Estás seguro de que deseas cambiar al plan <strong>{selectedPlan?.name}</strong>?
                            {selectedPlan && selectedPlan.monthlyPrice > 0 && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="body2" fontWeight="600">
                                        Precio: {formatPrice(selectedPlan.monthlyPrice)}/mes
                                    </Typography>
                                    <Typography variant="body2" fontWeight="600">
                                        {selectedPlan.documentsLimit ? `${selectedPlan.documentsLimit} documentos/mes` : 'Documentos ilimitados'}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="600">
                                        {selectedPlan.signersLimit ? `${selectedPlan.signersLimit} firmantes/doc` : 'Firmantes ilimitados'}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="600">
                                        {Math.round(selectedPlan.storageLimitMB / 1024)}GB almacenamiento
                                    </Typography>
                                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <img
                                            src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png"
                                            alt="PayPal"
                                            style={{ height: 20 }}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            Serás redirigido a PayPal para completar el pago de forma segura.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            {selectedPlan && selectedPlan.monthlyPrice === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    Este es el plan gratuito. Podrás cambiarlo en cualquier momento.
                                </Typography>
                            )}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    {validationErrors ? (
                        // Solo botón de cerrar si hay errores
                        <Button onClick={handleCloseModal} color="primary">
                            Entendido
                        </Button>
                    ) : (
                        // Botones de cancelar y confirmar
                        <>
                            <Button onClick={handleCloseModal} disabled={changingPlan}>
                                Cancelar
                            </Button>
                            <Button 
                                onClick={handleConfirmPlanChange} 
                                variant="contained" 
                                disabled={changingPlan}
                                autoFocus
                            >
                                {changingPlan
                                    ? <CircularProgress size={24} />
                                    : selectedPlan?.monthlyPrice > 0
                                        ? 'Ir a PayPal'
                                        : 'Confirmar Cambio'
                                }
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default PlansPage;
